import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { AnalysisResult, WordTimestamp } from '../types';
import { stripFencedCode } from '../utils/textUtils';

interface TranscriptRendererProps {
  result: AnalysisResult | null;
  wordTimestampsState: Array<WordTimestamp> | null;
  selectedWordIndex: number | null;
  onSetSelectedWordIndex: (index: number | null) => void;
  onSeekTo: (time?: number) => void;
}

const TranscriptRenderer: React.FC<TranscriptRendererProps> = ({
  result,
  wordTimestampsState,
  selectedWordIndex,
  onSetSelectedWordIndex,
  onSeekTo,
}) => {
  if (!result) return null;
  // If word-level timestamps available render clickable transcript
  if (wordTimestampsState && Array.isArray(wordTimestampsState)) {
  const plain = String(stripFencedCode(result.analysis || '')).trim();

    const normalize = (s: string) =>
      String(s || '')
        .toLowerCase()
        .replace(/[""«»„‟]/g, '"')
        .replace(/[''`]/g, "'")
        .replace(/[^a-z0-9'\u00C0-\u017F]+/gi, '');

    type TsWord = { idx: number; text: string; norm: string; start: number; used: boolean };
    const tsWords: TsWord[] = (wordTimestampsState || []).map((w, idx: number) => {
      const obj = w as Record<string, unknown>;
      const text = String(obj.text ?? obj.word ?? '');
      return {
        idx,
        text,
        norm: normalize(text),
        start: Number(obj.start ?? 0),
        used: false,
      };
    });

    const paragraphs = plain.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    if (paragraphs.length > 0) paragraphs[0] = paragraphs[0].replace(/^\s*TRANSCRIPTION\.?\s*/i, '').trim();

    let globalTsPtr = 0;

    const renderParagraph = (p: string, pIndex: number) => {
      const parts: Array<JSX.Element | string> = [];
      const tokenRegex = /([A-Za-z0-9\u00C0-\u017F''-]+|[^A-Za-z0-9\u00C0-\u017F''-]+)/g;
      let match: RegExpExecArray | null;
      while ((match = tokenRegex.exec(p)) !== null) {
        const token = match[0];
        const isWord = /[A-Za-z0-9\u00C0-\u017F''-]/.test(token);
        if (!isWord) { parts.push(token); continue; }

        const tokenNorm = normalize(token);

        let found: { idx: number; start: number } | null = null;
        for (let k = globalTsPtr; k < tsWords.length; k++) {
          if (!tsWords[k].norm || tsWords[k].used) continue;
          if (tsWords[k].norm === tokenNorm || tsWords[k].norm.startsWith(tokenNorm) || tokenNorm.startsWith(tsWords[k].norm)) {
            found = { idx: tsWords[k].idx, start: tsWords[k].start };
            tsWords[k].used = true;
            globalTsPtr = k + 1;
            break;
          }
        }

        if (!found) {
          const window = 8;
          let best: { idx: number; start: number; score: number } | null = null;
          for (let k = Math.max(0, globalTsPtr - 2); k < Math.min(tsWords.length, globalTsPtr + window); k++) {
            if (tsWords[k].used) continue;
            const cand = tsWords[k];
            let score = 0;
            if (cand.norm === tokenNorm) score = 100;
            else if (cand.norm.startsWith(tokenNorm) || tokenNorm.startsWith(cand.norm)) score = 80;
            else if (cand.norm[0] === tokenNorm[0]) score = 30;
            if (score > 0) {
              const timeDiff = Math.abs((cand.start || 0) - (tsWords[Math.max(0, globalTsPtr - 1)]?.start || 0));
              const combined = score - Math.min(timeDiff, 50);
              if (!best || combined > best.score) best = { idx: cand.idx, start: cand.start, score: combined };
            }
          }
          if (best) {
            const b = best;
            found = { idx: b.idx, start: b.start };
            const usedIdx = tsWords.findIndex(t => t.idx === b.idx);
            if (usedIdx >= 0) tsWords[usedIdx].used = true;
            globalTsPtr = Math.max(globalTsPtr, usedIdx + 1);
          }
        }

        if (found) {
          const idx = found.idx;
          const startTime = found.start;
          const wText = tsWords.find(t => t.idx === idx)?.text || token;
          parts.push(
            <span
              key={`w-${pIndex}-${parts.length}-${idx}`}
              role="button"
              tabIndex={0}
              onClick={() => { onSetSelectedWordIndex(idx); onSeekTo(startTime); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSetSelectedWordIndex(idx); onSeekTo(startTime); } }}
              className={`transition-colors duration-150 cursor-pointer ${selectedWordIndex === idx ? 'text-brand-yellow underline' : 'hover:underline'}`}
              title={`Jump to ${Number(startTime).toFixed(2)}s`}
              style={{ display: 'inline' }}
            >{wText}</span>
          );
        } else {
          parts.push(token);
        }
      }
      return <p key={`p-${pIndex}`} className="text-sm leading-relaxed mb-3">{parts}</p>;
    };

    return (
      <div className="space-y-3">
        <div className="prose prose-invert max-w-none">
          <h4 className="text-base font-semibold mb-3">Transcription</h4>
          <div className="leading-loose">
            {paragraphs.map((p, i) => renderParagraph(p, i))}
          </div>
        </div>
      </div>
    );
  }

  // Try to parse JSON summary
  const jsonMatch = result.analysis.match(/```json\s*([\s\S]*?)\s*```\s*$/i);
  if (jsonMatch) {
    try {
      const summary = JSON.parse(jsonMatch[1]);
      if (summary.observable_features || summary.potential_areas || summary.educational_information) {
        const renderList = (items: string[] | undefined) => (
          <ul className="list-disc pl-5 mb-3">{(items || []).map((it, i) => <li key={i} className="text-sm">{it}</li>)}</ul>
        );
        return (
          <div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">Observable Features</h4>
              {renderList(summary.observable_features)}
            </div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">Potential Areas of Concern</h4>
              {renderList(summary.potential_areas)}
            </div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">Educational Information</h4>
              {renderList(summary.educational_information)}
            </div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">Recommended Actions</h4>
              {renderList(summary.recommended_actions)}
            </div>
            <div className="mb-0">
              <h4 className="text-sm font-semibold mb-1">Limitations</h4>
              {renderList(summary.limitations)}
            </div>
          </div>
        );
      }
    } catch (e) {
      // fall through
    }
  }

  // Fallback: render markdown
  return (
    <ReactMarkdown
      components={{
        h1: ({node: _node, ...props}) => <h1 className="text-xl font-semibold mt-2 mb-2" {...props} />,
        h2: ({node: _node, ...props}) => <h2 className="text-lg font-semibold mt-4 mb-2" {...props} />,
        h3: ({node: _node, ...props}) => <h3 className="text-md font-medium mt-3 mb-1" {...props} />,
        p: ({node: _node, ...props}) => <p className="text-sm leading-relaxed mb-2" {...props} />,
        li: ({node: _node, ...props}) => <li className="mb-1" {...props} />,
        hr: ({node: _node, ...props}) => <hr className="my-3 border-border-secondary" {...props} />,
      }}
    >
      {result.analysis}
    </ReactMarkdown>
  );
};

export default TranscriptRenderer;
